import React, { useMemo } from 'react';

import { TextTypeKeys, TimeSlot } from 'src/constant';
import dayjs, { Dayjs } from 'dayjs';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { TableLayout } from '../../components/table_layout/table_layout';
import styles from './index.module.scss';
import { SearchPanel } from './search_panel';
import { useSessionStore } from '../../../context/session';
import { TaskListParam, useTaskListStore } from './tasklist_store';
import { useIndicator } from '../../../hook/use_indicator';
import { ApplicantResponse } from '../../../model/tasklist_response';
import { DataType } from './data_type';
import { ApiError } from '../../../api/api';
import { ConflictButton } from './conflict_button';
import { Settings } from '../../../constant/settings';
import { Messages } from '../../../constant/messages';

const devMode: string = '0';

/**
 * 作業競合確認画面
 * @constructor
 */
export const TaskListPage: React.FC = () => {
  const navigator = useNavigate();
  const { selectedDivisionId } = useSessionStore((selector) => selector);
  const { searchTaskList } = useTaskListStore((selector) => selector);
  const [data, setData] = React.useState<ApplicantResponse>([]);
  const [searchDate, setSearchDate] = React.useState<Dayjs>(null);
  const [errorCode, setErrorCode] = React.useState<number>(0);
  const [conflictTime, setConflictTime] = React.useState<Dayjs>(dayjs());
  const [searching, setSearching] = React.useState<boolean>(false);

  const indicator = useIndicator();
  const [param, setParam] = React.useState<TaskListParam>({
    sections: [],
    division: selectedDivisionId ?? 0,
    floor: 0,
    block: 0,
    applyDateType: 'range',
    apply_from: dayjs().add(-Settings.TASK_LIST.INITIAL_WORK_DAY, 'day').format('YYYY-MM-DD'),
    apply_to: dayjs().format('YYYY-MM-DD'),
    workDateType: 'none',
    work_from: undefined,
    work_to: undefined,
    timeType: 'none',
    start_time: undefined,
    end_time: undefined,
    span_of_time: TimeSlot.none,
    textType: TextTypeKeys.textTypeNone,
    content: '',
    count: 25,
  });

  const handleSearch = (param: TaskListParam) => {
    // @ts-ignore
    const newParam:TaskListParam = {};
    Object.entries(param).forEach(([key, value]) => {
      if (key === 'workDateType' || key === 'timeType' || key === 'applyDateType') {
        return;
      }
      if (key === 'textType' && value === '0') {
        return;
      }
      if (value !== undefined && value !== '' && value !== 0) {
        // @ts-ignore
        newParam[key] = value;
      }
    });

    if (param.timeType === 'timeSlot') {
      delete newParam.start_time;
      delete newParam.end_time;
    } else if (param.timeType === 'range') {
      delete newParam.span_of_time;
    } else {
      delete newParam.start_time;
      delete newParam.end_time;
      delete newParam.span_of_time;
    }
    indicator.show();
    setSearching(true);
    setErrorCode(0);
    setData([]);
    setSearchDate(null);
    setParam(param);
    searchTaskList(newParam).then((resp) => {
      setData(resp);
      setSearchDate(dayjs());
      setConflictTime(dayjs());
    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
          setErrorCode(status);
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
          setErrorCode(status);
        }
      }
    }).finally(() => {
      indicator.hide();
      setSearching(false);
    });
  };

  const taskData: DataType[] = useMemo(() => {
    return data.map((d) => { return { key: `${d.id}-${d.planId}`, ...d } as DataType; });
  }, [data]);

  return (
    <div>
      <h1>作業競合確認画面</h1>
      <SearchPanel defaultParam={param} onSearch={handleSearch} searching={searching} />
      <div className={styles.infoBox}>
        <ConflictButton searchTime={conflictTime}/>
        <div style={{ display: searchDate === null ? 'none' : 'block' }} >
          <div>
            検索結果: {data.length} 件 &nbsp;&nbsp;&nbsp;&nbsp;
            最終更新日時（検索時刻）: {searchDate?.format('YYYY/MM/DD HH:mm:ss')} &nbsp;&nbsp;&nbsp;&nbsp;
          </div>
        </div>
      </div>
      <TableLayout errorCode={errorCode} loading={false} devMode={devMode} data={taskData}/>
    </div>
  );
};
